import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import ExitIcon from '@/assets/svg/bx-exit.svg?react';
import { Button, LinkButton } from '@/components/buttons';
import { GlobalLoader } from '@/components/GlobalLoader';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import { ReturnToLink } from '@/components/ReturnToLink';
import { useLogOut } from '@/hooks/useLogOut';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

import UpdatePasswordDialog from './UpdatePasswordDialog';

type Props = {
  isC99Superuser: boolean;
  isEditing: boolean;
  isSaving: boolean;
  onEditChange: (isEditing: boolean) => void;
};

const ProfileHeader = ({ isC99Superuser, isEditing, isSaving, onEditChange }: Props) => {
  const user = useMe();
  const { logOut } = useLogOut();
  const [showUpdatePasswordDialog, setShowUpdatePasswordDialog] = useState(false);
  const [isLoggingOut, setLoggingOut] = useState(false);

  const userUsedPasswordAuth = user?.authFactors.some((factor) => factor.type === 'password');

  const handleLogOut = async () => {
    setLoggingOut(true);
    await logOut();
    setLoggingOut(false);
  };

  if (isLoggingOut) {
    return <GlobalLoader isLoggingOut />;
  }

  return (
    <div className="flex items-center justify-between gap-6 max-md:flex-col max-md:items-start max-md:justify-start max-md:gap-4">
      <ReturnToLink previousPage />
      <div className="flex items-center gap-4 max-md:w-full max-md:justify-end">
        <PendoPlaceholder />
        {isEditing ? (
          <>
            <Button disabled={isSaving} onClick={() => onEditChange(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button variant="primary" type="submit" isLoading={isSaving}>
              <Trans>Save</Trans>
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => onEditChange(true)}>
              <Trans>Edit Profile</Trans>
            </Button>
            {userUsedPasswordAuth && (
              <Button onClick={() => setShowUpdatePasswordDialog(true)}>
                <Trans>Update Password</Trans>
              </Button>
            )}
            {isC99Superuser && (
              <LinkButton to={ROUTES.loginAs.path}>
                <Trans>Login As</Trans>
              </LinkButton>
            )}
            <Button danger onClick={handleLogOut}>
              <ExitIcon />
              <Trans>Log Out</Trans>
            </Button>
          </>
        )}
      </div>
      <UpdatePasswordDialog
        open={showUpdatePasswordDialog}
        onClose={() => setShowUpdatePasswordDialog(false)}
      />
    </div>
  );
};

export default ProfileHeader;
